/* Reconciliation definitions, their runs, and one run's masked breaks — over the real API.
 *
 * No `X-Lumiq-Tenant` header, here or anywhere in `src/`: hard rule 1, the edge sets the tenant from
 * a validated token claim.
 *
 * **Reads plus one write, and the write is the only one the API has.** `POST …/reconciliations` is a
 * 202 + `Location` (hard rule 6) — the full match is a join of a customer's tables, and the sketch
 * gate that precedes it is still two scans of them. Authoring a definition stays out of the UI
 * deliberately: it names two tables, their key columns and a per-field tolerance list an engineer
 * verifies against their own schema, and getting a key column wrong is precisely the input that
 * produces the `keys_disjoint` refusal this screen exists to explain. Publishing a tolerance version
 * is out for a sharper reason — a version is immutable and every past run cites it, so a mistyped
 * bound is permanent history rather than an edit somebody can undo.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  isBreak,
  isDefinition,
  isInvestigation,
  isRun,
} from '@/shared/api/generated/reconciliation';
import type {
  Break,
  Definition,
  Investigation,
  InvestigationScope,
  Run,
} from '@/shared/api/generated/reconciliation';

import {
  forgetIdempotencyKey,
  getOne,
  getPage,
  idempotencyKeyFor,
  post,
  type Page,
} from '@/shared/api/http';

import { isInFlight, orderRuns } from '../model/reconciliation';

export const DEFINITIONS_URL = '/v1/reconciliation/definitions';

/** How often a screen holding an in-flight run re-reads it.
 *
 * **Only while something is moving**, which is the whole design of the `refetchInterval`s below: a run
 * is a join of a customer's tables and takes minutes, so a screen that polled unconditionally would
 * read this platform's own database every few seconds for ever to watch nothing change. Ten seconds
 * because a reconciliation is not a progress bar — the useful signal is the state landing, not a
 * smooth count — and because the alternative is a reader pressing browser-refresh to find out whether
 * the run they just requested has started.
 */
const IN_FLIGHT_POLL_MS = 10_000;

/* The transport is `@/shared/api/http`, and the local re-implementation this replaced is why.
 * It unwrapped the envelope and guarded every item exactly as `getPage` does — and omitted the bearer
 * token, so this screen 401'd under ADR-012's local auth. Only the request moved; the validation is
 * unchanged, and it is not decoration: a cast would let a malformed run through with
 * `state: undefined`, and a tone lookup on that renders a badge with no words in it. */
function runsUrl(definitionId: string): string {
  return `${DEFINITIONS_URL}/${encodeURIComponent(definitionId)}/reconciliations`;
}

/** The definitions, **with the envelope kept**.
 *
 * `meta.truncated` says the API returned a bounded page, and it has been on the wire since `page()`
 * gained it while every screen here discarded it — so a tenant past the cap saw a complete-looking
 * list. The board bands it. Returning the envelope rather than the items is what makes that
 * possible without a second read.
 */
export function useDefinitions(): UseQueryResult<Page<Definition>> {
  return useQuery({
    queryKey: ['reconciliation', 'definitions'],
    queryFn: () => getPage(DEFINITIONS_URL, isDefinition, 'reconciliation definition'),
    retry: false,
  });
}

/** One definition — the only route that carries its latest tolerance version.
 *
 * **A second read rather than a wider list response, because the contract says so**:
 * `tolerance_version` is documented as null in list responses and the router passes `None` for every
 * row it emits. So the definitions table cannot report which tolerance is in force without asking per
 * definition, and the runs screen — whose whole left panel is that version — needs this read whatever
 * the list did.
 *
 * Exported as a query *object* so the list screen can batch a bounded number of them through
 * `useQueries`: the count of definitions is data, and a hook count cannot be.
 */
export function definitionQuery(definitionId: string): {
  readonly queryKey: readonly unknown[];
  readonly queryFn: () => Promise<Definition>;
  readonly retry: false;
} {
  return {
    queryKey: ['reconciliation', 'definition', definitionId],
    queryFn: () =>
      getOne(
        `${DEFINITIONS_URL}/${encodeURIComponent(definitionId)}`,
        isDefinition,
        'reconciliation definition',
      ),
    retry: false,
  };
}

export function useDefinition(definitionId: string): UseQueryResult<Definition> {
  return useQuery(definitionQuery(definitionId));
}

/** A definition's runs, newest first.
 *
 * Ordered on this side of the wire as well as promised on the other: both screens read `[0]` as "the
 * last run", and that is a claim worth holding where it is read rather than trusting a route
 * description. */
export function runsQuery(definitionId: string): {
  readonly queryKey: readonly unknown[];
  readonly queryFn: () => Promise<Page<Run>>;
  readonly retry: false;
} {
  return {
    queryKey: ['reconciliation', 'runs', definitionId],
    queryFn: async () => {
      const page = await getPage(runsUrl(definitionId), isRun, 'reconciliation run');
      return { ...page, items: orderRuns(page.items) };
    },
    retry: false,
  };
}

export function useRuns(definitionId: string): UseQueryResult<Page<Run>> {
  return useQuery({
    ...runsQuery(definitionId),
    /* Read from the data this query already holds, so the interval switches itself off when the last
     * run settles — no effect, no timer to clear, and nothing to get wrong on unmount. */
    refetchInterval: ({ state }) =>
      (state.data?.items ?? []).some((run) => isInFlight(run.state))
        ? IN_FLIGHT_POLL_MS
        : false,
  });
}

/** One run, for the breaks screen's own header and truncation arithmetic.
 *
 * The breaks screen is reachable by a URL, so it cannot assume the runs list is in cache — and it
 * needs the run's *exact* counts to say what fraction of them the loaded list holds. */
export function useRun(definitionId: string, runId: string): UseQueryResult<Run> {
  return useQuery({
    queryKey: ['reconciliation', 'run', definitionId, runId],
    queryFn: () =>
      getOne(
        `${runsUrl(definitionId)}/${encodeURIComponent(runId)}`,
        isRun,
        'reconciliation run',
      ),
    retry: false,
    refetchInterval: ({ state }) =>
      state.data !== undefined && isInFlight(state.data.state)
        ? IN_FLIGHT_POLL_MS
        : false,
  });
}

/** One run's masked breaks.
 *
 * Bounded at 200 by the API, and `breaks_truncated` on the *run* is what says the list was cut — so
 * this deliberately does not infer truncation from `items.length === 200`. A run with exactly 200
 * breaks is not truncated, and guessing would put a warning band over a complete list.
 */
export function useBreaks(
  definitionId: string,
  runId: string,
): UseQueryResult<Page<Break>> {
  return useQuery({
    queryKey: ['reconciliation', 'breaks', definitionId, runId],
    queryFn: () =>
      getPage(
        `${runsUrl(definitionId)}/${encodeURIComponent(runId)}/breaks`,
        isBreak,
        'reconciliation break',
      ),
    retry: false,
  });
}

/** Request a run: the 202, and the only mutation this feature has.
 *
 * **Keyed on the definition rather than minted per click**, which is what a double-clicked button
 * needs: `idempotencyKeyFor` returns the same key until the request lands, so the second press is a
 * replay of the first rather than a second full join against a customer's database. The key is
 * forgotten on success, so the *next* deliberate run is a new request and not a replay of the one
 * before it — which is the half that makes re-running after a fix possible at all.
 *
 * The refusals are the server's own and are rendered verbatim: a 409 means a run is already in flight
 * for this definition, and a 422 names the missing similarity function when a fuzzy tolerance meets a
 * source without one. Restating either here would be a second place deciding what they mean.
 *
 * Invalidated rather than patched optimistically. The 202's body *is* a real pending run, so a prepend
 * would be honest — but the same request also adopts an in-flight run older than six hours as failed,
 * so the authoritative list is the one the server has just rewritten.
 */
export function useRequestRun(): UseMutationResult<Run, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (definitionId: string) =>
      post(
        runsUrl(definitionId),
        idempotencyKeyFor('reconciliation-run', definitionId),
        isRun,
        'reconciliation run',
      ),
    onSuccess: (_run, definitionId) => {
      forgetIdempotencyKey('reconciliation-run', definitionId);
      void client.invalidateQueries({
        queryKey: ['reconciliation', 'runs', definitionId],
      });
    },
  });
}

/** The SQL a steward runs at their own source to see a run's break rows.
 *
 * **Fetched on demand and held for nothing.** `enabled` is false until somebody opens the panel, so a
 * screen that is only being read never asks for it. `gcTime: 0` — the statements are text about a
 * customer's tables, they cost nothing to regenerate and nobody needs them kept in a cache after the
 * dialog closes. The route reads no customer data and executes nothing; the refusals are the server's
 * own and rendered verbatim (a connection type SQL cannot be written for, a run that did not succeed).
 */
export function useInvestigation(
  definitionId: string,
  runId: string,
  options: {
    readonly scope: InvestigationScope;
    readonly allColumns: boolean;
    readonly enabled: boolean;
  },
): UseQueryResult<Investigation> {
  const query = new URLSearchParams({
    scope: options.scope,
    all_columns: String(options.allColumns),
  });
  return useQuery({
    queryKey: [
      'reconciliation',
      'investigation',
      definitionId,
      runId,
      options.scope,
      options.allColumns,
    ],
    queryFn: () =>
      getOne(
        `${runsUrl(definitionId)}/${encodeURIComponent(runId)}/investigation?${query.toString()}`,
        isInvestigation,
        'investigation SQL',
      ),
    enabled: options.enabled,
    retry: false,
    gcTime: 0,
  });
}
